// The wholesale application (/wholesale/apply): three short steps, checked as you go, then one POST to
// /api/wholesale-apply, which files it in Shopify. Without that endpoint (the local preview, or before its Shopify token
// is set) the form hands the same answers to the applicant's own email instead, and says so. Nothing is kept in the
// browser: names, email and phone never touch localStorage.
import { $, $$, initChrome } from 'v2/ui.js';
import { FLAVORS, WHOLESALE } from 'v2/catalog.js';

initChrome();

const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const form = $('[data-apply]');
const steps = $$('[data-step]', form);
const tabs = $$('[data-step-tab]');
const errorBox = $('[data-apply-error]');
const startedAt = Date.now();
let current = 1;

$('[data-states]').insertAdjacentHTML('beforeend', STATES.map((s) => `<option>${s}</option>`).join(''));
// Wholesale is the seven single flavors (WHOLESALE.items); the holiday can isn't sold into retail here.
$('[data-flavor-chips]').innerHTML = FLAVORS.filter((f) => WHOLESALE.items[f.id]).map((f) => `<label class="chip-check f-${f.id}"><input type="checkbox" name="flavors" value="${f.name}"><span>${f.name}</span></label>`).join('');

/* ---------- Checking a step ---------- */
const MESSAGES = {
  business: 'Add your business name.',
  type: 'Choose the kind of business.',
  locations: 'Choose how many locations.',
  address1: 'Add a street address.',
  city: 'Add a city.',
  state: 'Choose a state.',
  zip: 'Use a 5-digit ZIP code.',
  firstName: 'Add your first name.',
  lastName: 'Add your last name.',
  email: 'Use an email address like name@store.com.',
  phone: 'Add a phone number with area code.',
  volume: 'Choose a rough number of cases.',
  certify: 'Please confirm your business license to apply.',
};
const phoneOk = (v) => v.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length === 10;

function fieldError(input) {
  if (input.name === 'phone' && input.value.trim() && !phoneOk(input.value)) return MESSAGES.phone;
  return input.checkValidity() ? '' : MESSAGES[input.name] || input.validationMessage;
}
function showError(input, message) {
  const wrap = input.closest('.field');
  let note = $('.field__error', wrap);
  if (!message) {
    input.removeAttribute('aria-invalid');
    note?.remove();
    return;
  }
  input.setAttribute('aria-invalid', 'true');
  if (!note) {
    note = document.createElement('p');
    note.className = 'field__error';
    note.id = `${input.id}-error`;
    wrap.append(note);
    input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), note.id].filter(Boolean).join(' '));
  }
  note.textContent = message;
}
function checkStep(n) {
  let first = null;
  $$('input[required], select[required], textarea[required], input[name="phone"]', steps[n - 1]).forEach((input) => {
    const message = fieldError(input);
    showError(input, message);
    if (message && !first) first = input;
  });
  first?.focus();
  return !first;
}
// Once a field has been flagged, it clears as soon as it's fixed.
form.addEventListener('input', (e) => { if (e.target.getAttribute('aria-invalid')) showError(e.target, fieldError(e.target)); });
form.addEventListener('change', (e) => { if (e.target.getAttribute('aria-invalid')) showError(e.target, fieldError(e.target)); });

/* ---------- Moving between steps ---------- */
function go(n) {
  current = n;
  steps.forEach((s, i) => { s.hidden = i !== n - 1; });
  tabs.forEach((t, i) => {
    if (i === n - 1) t.setAttribute('aria-current', 'step'); else t.removeAttribute('aria-current');
    t.classList.toggle('is-done', i < n - 1);
  });
  const card = form.closest('.apply-card');
  if (card.getBoundingClientRect().top < 80) card.scrollIntoView({ block: 'start' });
  $('input, select, textarea', steps[n - 1])?.focus({ preventScroll: true });
}
form.addEventListener('click', (e) => {
  if (e.target.closest('[data-next]') && checkStep(current)) go(current + 1);
  if (e.target.closest('[data-back]')) go(current - 1);
});
// Finished steps can be revisited from the step list.
tabs.forEach((t, i) => t.addEventListener('click', () => { if (i < current - 1) go(i + 1); }));

/* ---------- Sending ---------- */
function answers() {
  const data = new FormData(form);
  const one = (k) => String(data.get(k) || '').trim();
  return {
    business: one('business'), type: one('type'), locations: one('locations'), website: one('website'),
    address1: one('address1'), city: one('city'), state: one('state'), zip: one('zip'),
    firstName: one('firstName'), lastName: one('lastName'), role: one('role'), email: one('email'), phone: one('phone'),
    placement: data.getAll('placement'), flavors: data.getAll('flavors'), volume: one('volume'), heard: one('heard'),
    resale: one('resale'), notes: one('notes'), certify: one('certify') === 'yes',
    company_site: one('company_site'), startedAt,
  };
}
// The application as plain text, for the email fallback.
function asText(a) {
  const rows = [
    ['Business', `${a.business} (${a.type}), ${a.locations} location${a.locations === '1' ? '' : 's'}`],
    ['Website or Instagram', a.website],
    ['Address', `${a.address1}, ${a.city}, ${a.state} ${a.zip}`],
    ['Contact', [`${a.firstName} ${a.lastName}`, a.role, a.email, a.phone].filter(Boolean).join(', ')],
    ['Where PRODA would go', a.placement.join(', ')],
    ['Flavors', a.flavors.join(', ')],
    ['Cases a month', a.volume],
    ['Resale certificate', a.resale],
    ['Heard about PRODA', a.heard],
    ['Notes', a.notes],
    ['Valid business license', a.certify ? 'Yes, certified' : 'No'],
  ];
  return `Wholesale application\n\n${rows.filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n')}\n`;
}

function done({ kicker, title, lede, actions }) {
  form.hidden = true;
  $('[data-apply-steps]').hidden = true;
  const box = $('[data-apply-done]');
  $('[data-done-kicker]', box).textContent = kicker;
  $('[data-done-title]', box).textContent = title;
  $('[data-done-lede]', box).textContent = lede;
  $('[data-done-actions]', box).innerHTML = actions;
  box.hidden = false;
  box.focus();
  box.scrollIntoView({ block: 'center' });
}

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  errorBox.hidden = true;
  for (let n = 1; n <= steps.length; n++) {
    if (!checkStep(n)) { if (n !== current) { go(n); checkStep(n); } return; }
  }
  const a = answers();
  if (a.company_site) return; // a bot filled the hidden field
  const button = $('[data-submit]', form);
  button.disabled = true;
  button.textContent = 'Sending…';
  let res = null;
  try {
    res = await fetch('/api/wholesale-apply', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(a) });
  } catch { /* offline, or no endpoint here */ }
  const reply = res ? await res.json().catch(() => null) : null;

  if (res?.ok && reply?.ok) {
    done({
      kicker: 'Application received',
      title: `Thanks, ${a.firstName}.`,
      lede: `We review applications within a few business days and will email ${a.email} with next steps.`,
      actions: '<a class="btn btn--ink" href="/pages/wholesale">Back to wholesale</a>',
    });
    return;
  }
  if (res?.status === 400 && reply?.errors) {
    // The server's checks: show them where they belong.
    let firstStep = null;
    Object.entries(reply.errors).forEach(([name, message]) => {
      const input = form.elements[name];
      if (!input?.closest) return;
      showError(input, message);
      const n = Number(input.closest('[data-step]').dataset.step);
      firstStep = firstStep ? Math.min(firstStep, n) : n;
    });
    button.disabled = false;
    button.textContent = 'Submit application';
    if (firstStep) go(firstStep);
    return;
  }
  // No endpoint yet (or it failed): the applicant sends the same answers from their own email.
  const subject = `Wholesale application: ${a.business}`;
  const mailto = `mailto:${WHOLESALE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(asText(a))}`;
  done({
    kicker: 'One more step',
    title: 'Send it from your email.',
    lede: `We couldn’t file your application from here, so it’s ready to send from your own email to ${WHOLESALE.email}. Nothing has been sent yet.`,
    actions: `<a class="btn btn--orange" href="${mailto}">Email my application</a><button class="btn btn--ghost" type="button" data-copy>Copy my answers</button>`,
  });
  $('[data-copy]')?.addEventListener('click', async (ev) => {
    try { await navigator.clipboard.writeText(asText(a)); ev.target.textContent = 'Copied'; } catch { ev.target.textContent = 'Copy failed'; }
  });
});
